'use client';

import { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import ContactForm from '../components/ContactForm';
import { DefaultSeo } from 'next-seo';
import SEOHead from '../components/SEOHead';
import * as m from 'framer-motion/m';
import ContactFaqs from '../components/ContactFaqs';
import ContactBusinessInfo from '../components/ContactBusinessInfo';

const ContactPage = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <SEOHead
        title='Contact | LBVisible'
        description='Get in touch with LBVisible for custom website development, SEO, and digital marketing solutions.'
        canonicalUrl='/contact'
        lang='en'
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How long does it take to launch my website?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Most websites are delivered within 5–10 business days after we receive your completed project brief. If your plan includes more features (e.g., CMS, Stripe), it may take slightly longer.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do I need to provide all the content?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'You provide the basics—like company info, services, and any branding assets. We help you structure everything and offer copy guidance if needed.',
              },
            },
            {
              '@type': 'Question',
              name: 'What happens after the website is live?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'After launch, you can log in anytime to manage content through the CMS (if included). We also provide support for updates, improvements, or scaling your site as you grow.',
              },
            },
            {
              '@type': 'Question',
              name: 'How long does it take to see results in Google Maps (GBP Ranking)?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'For local ranking (GBP), early signs of movement often appear within 30–60 days. However, reaching the Local Pack (Top 3) typically takes 3–6 months depending on your competition and location.',
              },
            },
            {
              '@type': 'Question',
              name: 'What is the difference between SEO and GBP Ranking?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'SEO (Search Engine Optimization) focuses on ranking your website in general search results. GBP Ranking (Local SEO) targets the Map Pack and local searches so customers in your physical area find you first.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do I need a new website for SEO to work?',
              acceptedAnswer: {
                '@type': 'Answer',
                text:
                  'Not necessarily. We can optimize your existing site. However, if your current site is slow or not mobile-friendly, we may suggest a Growth Engine upgrade so Google does not penalize your rankings.',
              },
            },
            {
              '@type': 'Question',
              name: 'I cannot register or login to my account. What should I do?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'First, make sure you have verified your email address via the link sent to your inbox. If you still face issues, use the contact form on this page or email info@lbvisible.com and we will fix it within 24 hours.',
              },
            },
          ],
        }}
      />

      <DefaultSeo
        title='Contact | LBVisible'
        description='Get in touch with LBVisible for custom website development, SEO, and digital marketing solutions.'
        openGraph={{
          type: 'website',
          locale: 'en_US',
          url: 'https://www.lbvisible.com/contact',
          site_name: 'LBVisible',
          title: 'Contact | LBVisible',
          description: 'Get in touch with LBVisible for custom website development, SEO, and digital marketing solutions.',
          images: [
            {
              url: 'https://www.lbvisible.com/ogbanners/ogbanner_lbv.jpg',
              width: 1200,
              height: 630,
              alt: 'LB Visible Website & CMS Preview',
            },
          ],
        }}
      />

      <m.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className='w-full pt-12 sm:pt-24 pb-20 px-4 flex justify-center'
      >
        {loading ? (
          <div className='flex justify-center items-center min-h-[50vh]'>
            <Spinner />
          </div>
        ) : (
          <div className='w-full max-w-5xl flex flex-col gap-10'>
            <ContactForm />
            <ContactFaqs />
            <ContactBusinessInfo />
          </div>
        )}
      </m.div>
    </>
  );
};

export default ContactPage;
